/**
 * Storage-key plumbing for the designer's local recovery draft (AB#2315), split out of
 * `printTemplateDraftIO.ts` so the logout path (`hooks/useLogout.ts`) can clear a user's drafts
 * without importing the designer's validation/render-core dependency graph.
 *
 * Key shape: `<prefix>.<userKey>.<tenantId>.<scopeKey>`. The user segment (AB#2357) is what keeps
 * a second person who signs in to the same tenant on a shared browser from being offered the
 * previous person's unsaved design.
 */

export const LOCAL_DRAFT_STORAGE_PREFIX = 'beetegre.print-template-designer.draft';

/** Designer-local ruler guides (`printTemplateCanvasGuides.ts`) share the draft's
 *  `<prefix>.<userKey>.<tenantId>.<scopeKey>` shape, so the same logout sweep covers them. */
export const LOCAL_GUIDES_STORAGE_PREFIX = 'beetegre.print-template-canvas.guides.';

/** Pre-AB#2357 keys had no user segment: `<prefix>.<tenantId (GUID)>.<scopeKey>`. Never read any
 *  more; recognised here only so logout can sweep them instead of leaving them to the 30-day TTL. */
const LEGACY_DRAFT_KEY_PATTERN = new RegExp(
  `^${LOCAL_DRAFT_STORAGE_PREFIX.replaceAll('.', '[.]')}[.][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[.]`,
  'i',
);

/** Stable per-user segment. The auth payload carries no user id (`LoginResponse` exposes `Email`
 *  only), so the normalised email is the identity. Dots are percent-encoded too, so one user's
 *  prefix (`a`) can never be a segment-prefix of another's (`a.b`). */
export function printTemplateDraftUserKey(email: string): string {
  return encodeURIComponent(email.trim().toLowerCase()).replace(/\./g, '%2E');
}

/** User-, tenant- and template-scoped storage key — `scopeKey` is the SAME identity string the
 *  designer page's `key` prop uses (`template.id` in EDIT mode, `new-${documentType}` in CREATE). */
export function printTemplateDraftStorageKey(userEmail: string, tenantId: string, scopeKey: string): string {
  return `${LOCAL_DRAFT_STORAGE_PREFIX}.${printTemplateDraftUserKey(userEmail)}.${tenantId}.${scopeKey}`;
}

/** Removes every designer draft and ruler-guide set that belongs to `userEmail` (all tenants, all
 *  templates) plus every legacy user-less draft. Other users' current-format keys are left alone.
 *  Never throws. */
export function clearPrintTemplateLocalDraftsForUser(userEmail: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    const userKey = userEmail ? printTemplateDraftUserKey(userEmail) : null;
    const userPrefixes = userKey ? [`${LOCAL_DRAFT_STORAGE_PREFIX}.${userKey}.`, `${LOCAL_GUIDES_STORAGE_PREFIX}${userKey}.`] : [];
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key === null) continue;
      if (userPrefixes.some((prefix) => key.startsWith(prefix)) || LEGACY_DRAFT_KEY_PATTERN.test(key)) doomed.push(key);
    }
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage disabled: nothing was written either, so nothing to clear.
  }
}

/** Users who explicitly signed out in this document. `clearPrintTemplateLocalDraftsForUser` runs
 *  BEFORE the designer unmounts, and the unmount flush must not re-create the draft it just swept.
 *  The auth store cannot carry that signal: a forced session expiry (`lib/api.ts` redirectToLogin)
 *  also nulls the user, and that path must still flush so the user can recover after signing back in. */
const explicitlySignedOutUserKeys = new Set<string>();

/** Sweeps the user's drafts and blocks any later flush for them until `allowPrintTemplateDraftWritesForUser`. */
export function discardPrintTemplateLocalDraftsOnLogout(userEmail: string | null | undefined): void {
  if (userEmail) explicitlySignedOutUserKeys.add(printTemplateDraftUserKey(userEmail));
  clearPrintTemplateLocalDraftsForUser(userEmail);
}

export function isPrintTemplateDraftWriteBlocked(userEmail: string): boolean {
  return explicitlySignedOutUserKeys.has(printTemplateDraftUserKey(userEmail));
}

/** Called when a designer mounts for a signed-in user: a new session ends the sign-out block. */
export function allowPrintTemplateDraftWritesForUser(userEmail: string): void {
  explicitlySignedOutUserKeys.delete(printTemplateDraftUserKey(userEmail));
}
